<template>
  <v-sheet color="white" height="100%" min-width="380px" max-width="500px">
    <v-container fluid fill-width class="pa-0 ma-0">
      <!-- Variable icon/title/reset button -->
      <v-row
        class="subheading primary--text text--darken-4 pa-0 ma-0 text-truncate not-highlight-var pt-1"
      >
        <v-col cols="12" class="ma-0 pa-0">
          <v-card flat>
            <v-card-title class="primary--text pl-3 py-2">
              <img
                :src="'/images/' + variable.category + '-icon-128.png'"
                :title="variable.category"
                style="height: 3em;"
                @error="missingDomainFallback"
              />

              <div
                class="text-h6 ml-2 text-wrap pl-1"
                :style="max_width"
                style="word-break: keep-all;"
              >
                <v-tooltip top color="primary">
                  <template v-slot:activator="{ on: tooltip }">
                    <span v-on="{ ...tooltip }">
                      {{ getChartTitle(variable, 'abbreviation') }}
                    </span>
                  </template>
                  <span>{{ getChartTitle(variable, 'label') }}</span>
                </v-tooltip>
              </div>

              <v-spacer />

              <v-btn
                v-if="interactive"
                class="primary text--white ma-0 px-2 py-0"
                @click="resetClicked"
                >Reset</v-btn
              >
              <span
                v-if="
                  selectedAnalysisMode == 'Outcome based' &&
                    isBelowPValThreshold(variable)
                "
                class="text-h2"
                style="color: red;"
                >*</span
              >
            </v-card-title>
          </v-card>
        </v-col>
      </v-row>
      <!-- End of Variable icon/title/reset button -->
    </v-container>

    <v-container fluid fill-width class="pa-0 ma-0">
      <v-row class="pa-0 ma-0">
        <v-col cols="12" class="pa-0 ma-0">
          <div v-if="variable.value_type === 'date'" class="pl-3">
            Date types not supported.
          </div>
          <MultiVarBarChart
            v-else-if="
              !variable.is_longitudinal &&
                variable.data_category === 'Categorical' &&
                chartVariables.length > 1
            "
            :id="variable.id"
            :dimension-name="dimension"
            :variables="chartVariables"
            :show-filter-help="showFilterHelp"
            show-selection
            :interactive="interactive"
            @userChangedVariable="userChangedInputVariable"
          />
          <BarChart
            v-else-if="
              (!variable.is_longitudinal &&
                variable.data_category === 'Categorical') ||
                variable.type === 'study'
            "
            :id="variable.id"
            ref="col_chart"
            :dimension-name="dimension"
            :variable="variable"
            :show-filter-help="showFilterHelp"
            show-selection
            :interactive="interactive"
            @userChangedVariable="userChangedInputVariable"
          />
          <HistogramChart
            v-else-if="variable.data_category !== 'Categorical'"
            :id="variable.id"
            ref="hist_chart"
            :dimension-name="dimension"
            :variable="variable"
            :show-filter-help="showFilterHelp"
            :show-analytics-help="showAnalyticsHelp"
            input-variable
            :show-best-and-worst="
              variable.type == 'observation' &&
                (!variable.is_longitudinal ||
                  (variable.label == 'First Visit' ||
                    variable.label == 'Last Visit'))
            "
            :interactive="interactive"
            @userChangedVariable="userChangedInputVariable"
            @comparePredefinedRanges="comparePredefinedRanges"
            @savePredefinedRanges="savePredefinedRanges"
          />
          <BarChart
            v-else
            :id="variable.id"
            :dimension-name="dimension"
            :variable="variable"
            :show-filter-help="showFilterHelp"
            show-selection
            :interactive="interactive"
            @userChangedVariable="userChangedInputVariable"
          />
        </v-col>
      </v-row>
    </v-container>
  </v-sheet>
</template>

<script>
import BarChart from '@/components/CohortManager/BarChart/BarChart.vue';
import MultiVarBarChart from '@/components/CohortManager/BarChart/MultiVarBarChart.vue';
import HistogramChart from '@/components/CohortManager/HistogramChart/HistogramChart.vue';
import { mapActions, mapState } from 'vuex';
import { actions, state } from '@/store/modules/cohortManager/types';
import {
  getSetValuedVariableGroups,
  missingDomainFallback,
} from '@/utils/helpers';

export default {
  components: {
    BarChart,
    MultiVarBarChart,
    HistogramChart,
  },
  props: {
    variable: {
      type: Object,
      required: true,
    },
    showFilterHelp: {
      type: Boolean,
      required: false,
      default: false,
    },
    showAnalyticsHelp: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      dimension: null,
      subjectVariableGroups: { groups: [], var2group: {}, var2var: {} },
      observationVariableGroups: { groups: [], var2group: {}, var2var: {} },
      chartVariables: [],
      missingDomainFallback: missingDomainFallback,
    };
  },
  computed: {
    ...mapState('cohortManager', {
      collection: state.COLLECTION,
      dimensions: state.DIMENSIONS,
      unfilteredData: state.UNFILTERED_DATA,
      useLongScaleNames: state.USE_LONG_SCALE_NAMES,
      selectedAnalysisMode: state.SELECTED_ANALYSIS_MODE,
      pvals: state.PVALS,
      odds_ratio: state.ODDS_RATIO,
      pval_threshold: state.PVAL_THRESHOLD,
      currentAnalyticsTab: state.CURRENT_ANALYTICS_TAB,
    }),
    interactive() {
      return this.selectedAnalysisMode == 'Predictor based';
    },
    max_width() {
      if (this.interactive) {
        return 'max-width: 55%;';
      } else {
        return 'max-width: 80%;';
      }
    },
  },
  created() {
    this.subjectVariableGroups = getSetValuedVariableGroups(
      this.collection.subject_variables_list.map(sv => sv.ontology)
    );
    this.observationVariableGroups = getSetValuedVariableGroups(
      this.collection.observation_variables_list.map(ov => ov.ontology)
    );
    this.addDimensionHelper(this.variable);
  },
  methods: {
    ...mapActions('cohortManager', {
      addDimension: actions.ADD_DIMENSION,
      clearFilter: actions.CLEAR_FILTER,
    }),
    resetClicked() {
      this.clearFilter({ dimension: this.dimension });
      this.$emit('userResetInputVariable', this.dimension);
    },
    userChangedInputVariable() {
      this.$emit('userChangedInputVariable', this.dimension);
    },
    // returns all variables in the group for grouped vars
    getChartVariables(v) {
      let v_list = [];
      const sv2g = this.subjectVariableGroups['var2group'];
      const ov2g = this.observationVariableGroups['var2group'];

      if (v.label in sv2g) {
        sv2g[v.label]['vars'].forEach(v => {
          v_list.push(v.var);
        });
      } else if (v.label in ov2g) {
        ov2g[v.label]['vars'].forEach(v => {
          v_list.push(v.var);
        });
      } else {
        v_list.push(v);
      }
      return v_list;
    },
    getChartTitle(v, which) {
      let which_final = null;
      if (which == 'abbreviation') {
        which_final = this.useLongScaleNames ? 'label' : 'abbreviation';
      } else {
        which_final = this.useLongScaleNames ? 'description' : 'label';
      }
      var title = v[which_final];

      // grouped categorical/tag variables
      let chartVars = [...this.chartVariables];
      if (chartVars.length > 1) {
        chartVars.sort().forEach(cv => {
          let m = cv[which].match(/^((\S+)_(org)?)(\d+)$/i);
          if (m) {
            if (m[4] == 1) {
              title = m[1];
              if (title.endsWith('_')) {
                title = title.substring(0, title.length - 1);
              }
            }
          }
        });
      }

      if (v.type == 'observation' && v.is_longitudinal) {
        var fv = null;
        var lv = null;
        var p_name = null;

        this.collection.observation_variables_list.forEach(ov => {
          if (ov.ontology.id == v.parentID) {
            if (ov['first_visit_event']) {
              fv = ov['first_visit_event'];
              lv = ov['last_visit_event'];
              p_name = ov['ontology'][which_final];
            } else {
              fv = ov['first_visit_num'];
              lv = ov['last_visit_num'];
              p_name = ov['ontology'][which_final];
            }
          }
        });
        if (v.label == 'First Visit') {
          title = p_name + ' - ' + (which == 'label' ? 'Visit=' : '') + fv;
        } else if (this.variable.label == 'Last Visit') {
          title = p_name + ' - ' + (which == 'label' ? 'Visit=' : '') + lv;
        } else {
          var label = v.label;
          if (label == 'Rate of Change' && which == 'abbreviation') {
            label = 'Rate of Change';
          }

          title = p_name + ' - ' + label + ': ' + fv + '-' + lv;
        }
      }
      return title;
    },
    addDimensionHelper(variable) {
      var dimensionName = null;
      var payload = null;

      // observation variable
      if (variable.type === 'observation') {
        var dimensionId = null;
        var measure = null;

        if (variable.is_longitudinal) {
          measure = variable.id.split('-')[0];
          dimensionName = `${variable.parentLabel} - ${variable.label}`;
          dimensionId = variable.parentID;
        } else {
          measure = 'value';
          dimensionName = variable.label;
          dimensionId = variable.id;
        }

        let cvars = this.getChartVariables(variable);
        let ncv = cvars.length;
        this.chartVariables = cvars;

        // multiple/grouped variables
        if (ncv > 1) {
          payload = {
            dimensionName,
            accessor: d => {
              return cvars.map(cv =>
                cv.is_longitudinal ? d[cv.parentID][measure] : d[cv.id][measure]
              );
            },
            isArray: true,
          };
        } else {
          payload = {
            dimensionName,
            accessor: d => {
              return d[dimensionId][measure];
            },
            isArray: false,
          };
        }
      }
      // subject variable
      else {
        if (variable.label === 'Study' || variable.label === 'Dataset') {
          dimensionName = variable.label;
          payload = {
            dimensionName: dimensionName,
            accessor: d => d.study.study_name,
            isArray: false,
          };
        } else {
          let cvars = this.getChartVariables(variable);
          let ncv = cvars.length;
          this.chartVariables = cvars;

          // multiple/grouped variables
          if (ncv > 1) {
            dimensionName = variable.label;
            let varLabels = cvars.map(cv => cv.label);
            payload = {
              dimensionName: dimensionName,
              accessor: d => {
                return varLabels.map(vl => d[vl]);
              },
              isArray: true,
            };
          } else {
            dimensionName = variable.label;
            payload = {
              dimensionName: dimensionName,
              accessor: d => {
                const dimName = dimensionName;
                return d[dimName];
              },
              isArray: false,
            };
          }
        }
      }
      this.dimension = dimensionName;
      this.addDimension(payload);
    },
    isBelowPValThreshold(v) {
      const pvt = this.pval_threshold;
      let rv = false;
      if (!this.pvals) {
        return false;
      }
      if (this.currentAnalyticsTab == 0) {
        this.pvals.forEach(pv => {
          if (pv.label === v.label || pv.label === v.parentLabel) {
            if (pv.pval != null && pv.pval < pvt) {
              rv = true;
            }
          }
        });
      }

      return rv;
    },
    comparePredefinedRanges(ranges) {
      this.$emit('comparePredefinedRanges', ranges);
    },
    savePredefinedRanges(ranges) {
      this.$emit('savePredefinedRanges', ranges);
    },
  },
};
</script>

<style lang="scss" scoped></style>
